<template>
  <div class="fin-card">
    <div class="fin-filter-grid">
      <div class="fin-field">
        <label>Kërko produkt</label>
        <div class="fin-search-wrap">
          <i class="fa fa-search fin-search-icon"></i>
          <input v-model="search" @input="onSearchInput" class="fin-input" type="text" placeholder="Kërko produkt, kod ose barkod..." />
        </div>
      </div>

      <div class="fin-field">
        <label>Grupimi</label>
        <select class="fin-select" :value="modelValue.group_by" @change="update({ group_by: $event.target.value, pos_ids: [], warehouse_ids: [] })">
          <option value="pos">Pika shitje</option>
          <option value="warehouse">Magazina</option>
          <option value="product">Produkte (të gjitha magazinat)</option>
        </select>
      </div>

      <div class="fin-field" v-if="modelValue.group_by !== 'product'">
        <label>{{ modelValue.group_by === "pos" ? "Pikat e shitjes" : "Magazinat" }}</label>
        <fin-search-select
          v-if="modelValue.group_by === 'pos'"
          :options="posOptions"
          :model-value="modelValue.pos_ids"
          multiple
          all-label="Të gjitha pikat"
          empty-label="Nuk ka pika shitje."
          @update:modelValue="(val) => update({ pos_ids: val })"
        />
        <fin-search-select
          v-else
          :options="warehouseOptions"
          :model-value="modelValue.warehouse_ids"
          multiple
          all-label="Të gjitha magazinat"
          empty-label="Nuk ka magazina."
          @update:modelValue="(val) => update({ warehouse_ids: val })"
        />
      </div>

      <div class="fin-field">
        <label>Analiza e produktit</label>
        <div class="fin-segmented">
          <button type="button" :class="{ active: modelValue.metric !== 'both' }" @click="update({ metric: 'qty' })">Sipas sasisë</button>
          <button type="button" :class="{ active: modelValue.metric === 'both' }" @click="update({ metric: 'both' })">Sasi, Kosto & Vlera</button>
        </div>
      </div>

      <div class="fin-field">
        <label>Stok</label>
        <div class="fin-segmented">
          <button type="button" :class="{ active: modelValue.stock_filter === 'all' }" @click="update({ stock_filter: 'all' })">Të gjitha</button>
          <button type="button" :class="{ active: modelValue.stock_filter === 'with' }" @click="update({ stock_filter: 'with' })">Me stok</button>
          <button type="button" :class="{ active: modelValue.stock_filter === 'without' }" @click="update({ stock_filter: 'without' })">Pa stok</button>
        </div>
      </div>

      <div class="fin-field">
        <label>Kategoria e produktit</label>
        <fin-search-select
          :options="productCategoryOptions"
          :model-value="modelValue.product_category_guids"
          multiple
          all-label="Të gjitha kategoritë"
          @update:modelValue="(val) => update({ product_category_guids: val })"
        />
      </div>

      <div class="fin-field">
        <label>Kategoria e aksesorit</label>
        <fin-search-select
          :options="accessoryCategoryOptions"
          :model-value="modelValue.accessory_category_guids"
          multiple
          all-label="Të gjitha kategoritë"
          @update:modelValue="(val) => update({ accessory_category_guids: val })"
        />
      </div>

      <div class="fin-field">
        <label>Furnitori</label>
        <fin-search-select
          :options="supplierOptions"
          :model-value="modelValue.supplier_guids"
          multiple
          all-label="Të gjithë furnitorët"
          empty-label="Nuk ka furnitorë."
          @update:modelValue="(val) => update({ supplier_guids: val })"
        />
      </div>

      <div class="fin-field">
        <label>Grupi i magazinave</label>
        <fin-search-select
          :options="warehouseGroupOptions"
          :model-value="modelValue.warehouse_group_guids"
          multiple
          all-label="Të gjitha grupet"
          @update:modelValue="(val) => update({ warehouse_group_guids: val })"
        />
      </div>

      <div class="fin-field">
        <label>Objekti i magazinës</label>
        <fin-search-select
          :options="facilityOptions"
          :model-value="modelValue.warehouse_facility_id ? String(modelValue.warehouse_facility_id) : ''"
          all-label="Të gjitha objektet"
          @update:modelValue="(val) => update({ warehouse_facility_id: val ? Number(val) : null })"
        />
      </div>

      <div class="fin-field">
        <label>Fushata / oferta</label>
        <fin-search-select
          :options="fushatOptions"
          :model-value="modelValue.fushat_guid || ''"
          all-label="Të gjitha produktet"
          @update:modelValue="(val) => update({ fushat_guid: val || null, campaign_guid: null })"
        />
      </div>

      <div class="fin-field" v-if="campaignOptions.length">
        <label>Oferta e fushatës</label>
        <fin-search-select
          :options="campaignOptions"
          :model-value="modelValue.campaign_guid || ''"
          all-label="Të gjitha ofertat e fushatës"
          @update:modelValue="(val) => update({ campaign_guid: val || null })"
        />
      </div>

      <div class="fin-field" v-if="showAsOf">
        <label>Gjendja më datë</label>
        <input class="fin-input" type="date" :value="modelValue.as_of || ''" @change="update({ as_of: $event.target.value || null })" />
      </div>
    </div>

    <div class="fin-preset-row">
      <button v-if="vitrineFacilityId" type="button" class="fin-chip-btn" :class="{ active: Number(modelValue.warehouse_facility_id) === Number(vitrineFacilityId) && !modelValue.vitrine_split }" @click="toggleVitrine">Vitrinë (objekt)</button>
      <button
        type="button"
        class="fin-chip-btn"
        :class="{ active: !!modelValue.vitrine_split }"
        title="Për çdo kod me stok ≥1: 1 copë vitrinë, pjesa tjetër jo vitrinë"
        @click="toggleVitrineSplit"
      >
        Vitrinë / Jo vitrinë
      </button>
      <button
        type="button"
        class="fin-chip-btn"
        :class="{ active: !!modelValue.web_available }"
        title="Produktet e shënuara për web, vetëm në magazinat web"
        @click="update({ web_available: !modelValue.web_available })"
      >
        Vetëm web
      </button>
      <button
        type="button"
        class="fin-chip-btn"
        :class="{ active: !!modelValue.has_offer }"
        title="Produkte me cmim shitje më të ulët se cmimi bazë (si «Ka ofertë aktuale» te produktet)"
        @click="update({ has_offer: !modelValue.has_offer })"
      >
        Në ofertë
      </button>
      <span v-if="modelValue.vitrine_split" class="fin-preset-hint">1 copë në vitrinë, pjesa tjetër në dyqan (jo vitrinë)</span>
      <span v-if="modelValue.web_available" class="fin-preset-hint">Produktet në web × magazinat e shënuara për web</span>
      <span v-if="modelValue.has_offer" class="fin-preset-hint">Cmimi i shitjes &lt; cmimi bazë. Nuk lidhet me fushatat.</span>
    </div>

    <div v-if="selectedChips.length" class="fin-chips">
      <span v-for="chip in selectedChips" :key="chip.key" class="fin-chip">
        {{ chip.label }}
        <button type="button" title="Hiq" @click="removeChip(chip)">&times;</button>
      </span>
    </div>

    <div class="fin-active-filters">
      <span class="fin-active-filters-label">Filtra aktivë</span>
      <template v-if="activeChips.length">
        <span v-for="chip in activeChips" :key="chip.key" class="fin-chip muted">
          {{ chip.label }}
          <button type="button" title="Hiq" @click="removeFilter(chip.key)">&times;</button>
        </span>
        <button type="button" class="fin-link-btn" @click="clearAll">Pastro filtrat</button>
      </template>
      <span v-else class="fin-summary-label">Asnjë filtër i zgjedhur.</span>
      <span class="fin-active-count">{{ activeChips.length }} filtra aktivë</span>
    </div>
  </div>
</template>

<script>
import FinSearchSelect from "./FinSearchSelect.vue";

export default {
  name: "FinancaFilters",
  components: { FinSearchSelect },
  props: {
    modelValue: { type: Object, required: true },
    warehouses: { type: [Array, Object], default: () => [] },
    pointsOfSale: { type: [Array, Object], default: () => [] },
    categories: { type: [Array, Object], default: () => [] },
    facilities: { type: Array, default: () => [] },
    fushat: { type: Array, default: () => [] },
    campaigns: { type: Array, default: () => [] },
    suppliers: { type: Array, default: () => [] },
    vitrineFacilityId: { type: [Number, String], default: null },
    showAsOf: { type: Boolean, default: false },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      search: this.modelValue.search || "",
      searchTimeout: null,
    };
  },
  computed: {
    posList() {
      return this.asList(this.pointsOfSale);
    },
    warehouseList() {
      const fromApi = this.asList(this.warehouses);
      if (fromApi.length) return fromApi;
      const byGuid = {};
      this.posList.forEach((pos) => {
        this.asList(pos.warehouses).forEach((warehouse) => {
          if (warehouse && warehouse.guid) byGuid[warehouse.guid] = warehouse;
        });
      });
      return Object.values(byGuid);
    },
    posOptions() {
      return this.posList.map((pos) => ({
        value: pos.guid,
        label: pos.name,
        searchText: pos.name,
      }));
    },
    warehouseOptions() {
      return this.warehouseList
        .filter((warehouse) => warehouse && warehouse.guid)
        .map((warehouse) => this.warehouseOption(warehouse))
        .sort((a, b) => String(a.label).localeCompare(String(b.label), "sq"));
    },
    selectedChips() {
      const chips = [];
      const posByGuid = Object.fromEntries(this.posList.map((pos) => [String(pos.guid), pos]));
      (this.modelValue.pos_ids || []).forEach((id) => {
        const pos = posByGuid[String(id)];
        chips.push({ key: "pos:" + id, kind: "pos", value: id, label: pos ? pos.name : id });
      });
      const warehouseByGuid = Object.fromEntries(this.warehouseList.map((warehouse) => [String(warehouse.guid), warehouse]));
      this.posList.forEach((pos) => {
        this.asList(pos.warehouses).forEach((warehouse) => {
          if (warehouse && warehouse.guid) warehouseByGuid[String(warehouse.guid)] = warehouse;
        });
      });
      (this.modelValue.warehouse_ids || []).forEach((id) => {
        const warehouse = warehouseByGuid[String(id)];
        chips.push({ key: "wh:" + id, kind: "warehouse", value: id, label: warehouse ? warehouse.name : id });
      });
      return chips;
    },
    productCategoryOptions() {
      return this.buildOptions("products");
    },
    accessoryCategoryOptions() {
      return this.buildOptions("accessories");
    },
    warehouseGroupOptions() {
      return this.buildOptions("warehouses");
    },
    supplierOptions() {
      return this.asList(this.suppliers)
        .filter((row) => row && row.guid)
        .map((row) => ({
          value: row.guid,
          label: row.name || row.code || row.guid,
          searchText: [row.name, row.code].filter(Boolean).join(" "),
        }));
    },
    facilityOptions() {
      return this.facilities.map((facility) => ({ value: String(facility.id), label: facility.name, searchText: facility.name }));
    },
    fushatOptions() {
      return this.fushat.map((item) => ({
        value: item.guid,
        label: item.name || item.code,
        searchText: [item.name, item.code].filter(Boolean).join(" "),
      }));
    },
    campaignOptions() {
      const fushatGuid = this.modelValue.fushat_guid;
      if (!fushatGuid) return [];
      return (this.campaigns || [])
        .filter((item) => item.fushat_guid === fushatGuid)
        .map((item) => ({ value: item.guid, label: item.label || item.guid, searchText: item.label || item.guid }));
    },
    activeChips() {
      const chips = [];
      if (this.modelValue.search) chips.push({ key: "search", label: 'Kërkim: "' + this.modelValue.search + '"' });
      if (this.modelValue.group_by === "pos" && (this.modelValue.pos_ids || []).length) {
        chips.push({ key: "pos_ids", label: "Pika shitje: " + this.modelValue.pos_ids.length });
      }
      if (this.modelValue.group_by === "warehouse" && (this.modelValue.warehouse_ids || []).length) {
        chips.push({ key: "warehouse_ids", label: "Magazinat: " + this.modelValue.warehouse_ids.length });
      }
      const importedProducts = this.modelValue.product_guids || [];
      if (importedProducts.length) {
        chips.push({ key: "product_guids", label: "Produkte nga skedari: " + importedProducts.length });
      }
      this.pushGuidChips(chips, "warehouse_group_guids", "Grupi");
      if (this.modelValue.warehouse_facility_id) {
        const facility = this.facilities.find((item) => Number(item.id) === Number(this.modelValue.warehouse_facility_id));
        chips.push({ key: "warehouse_facility_id", label: "Objekti: " + (facility ? facility.name : this.modelValue.warehouse_facility_id) });
      }
      if (this.modelValue.fushat_guid) {
        const fushat = this.fushat.find((item) => item.guid === this.modelValue.fushat_guid);
        chips.push({ key: "fushat_guid", label: "Fushata: " + (fushat ? fushat.name || fushat.code : this.modelValue.fushat_guid) });
      }
      if (this.modelValue.campaign_guid) {
        chips.push({ key: "campaign_guid", label: "Oferta e zgjedhur" });
      }
      if (this.modelValue.web_available) {
        chips.push({ key: "web_available", label: "Vetëm web" });
      }
      if (this.modelValue.has_offer) {
        chips.push({ key: "has_offer", label: "Në ofertë" });
      }
      if (this.modelValue.vitrine_split) {
        chips.push({ key: "vitrine_split", label: "Vitrinë / Jo vitrinë" });
      }
      if (this.modelValue.as_of) {
        chips.push({ key: "as_of", label: "Më datë: " + this.modelValue.as_of });
      }
      this.pushGuidChips(chips, "product_category_guids", "Kategoria");
      this.pushGuidChips(chips, "accessory_category_guids", "Aksesor");
      if ((this.modelValue.supplier_guids || []).length) {
        const ids = this.modelValue.supplier_guids;
        if (ids.length === 1) {
          const found = this.asList(this.suppliers).find((row) => row.guid === ids[0]);
          chips.push({ key: "supplier_guids", label: "Furnitori: " + (found ? found.name || found.code : ids[0]) });
        } else {
          chips.push({ key: "supplier_guids", label: "Furnitorë: " + ids.length });
        }
      }
      if (this.modelValue.stock_filter && this.modelValue.stock_filter !== "all") {
        chips.push({ key: "stock_filter", label: "Stok: " + (this.modelValue.stock_filter === "without" ? "Pa stok" : "Me stok") });
      }
      return chips;
    },
  },
  watch: {
    "modelValue.search"(value) {
      if (value !== this.search) this.search = value;
    },
  },
  beforeUnmount() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  },
  methods: {
    update(patch) {
      this.$emit("update:modelValue", { ...this.modelValue, ...patch, page: 1 });
    },
    onSearchInput() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.update({ search: this.search.trim() }), 350);
    },
    asList(value) {
      if (Array.isArray(value)) return value;
      if (value && typeof value === "object") return Object.values(value);
      return [];
    },
    uniqueWarehouses(list) {
      const byGuid = {};
      this.asList(list).forEach((warehouse) => {
        if (!warehouse || !warehouse.guid) return;
        const current = byGuid[warehouse.guid];
        if (!current) {
          byGuid[warehouse.guid] = { ...warehouse, type_ids: [warehouse.type_id] };
          return;
        }
        if (!current.type_ids.includes(warehouse.type_id)) current.type_ids.push(warehouse.type_id);
      });
      return Object.values(byGuid);
    },
    warehouseTypeName(typeId) {
      const types = {
        1: "Kryesore",
        2: "Rezervim",
        3: "Vitrinë",
        4: "Të prishura",
        5: "Furnizuese",
        6: "Web",
      };
      return types[Number(typeId)] || "";
    },
    warehouseOption(warehouse, posName) {
      const typeNames = (warehouse.type_ids || [warehouse.type_id]).map((id) => this.warehouseTypeName(id)).filter(Boolean);
      return {
        value: warehouse.guid,
        label: warehouse.name || warehouse.code || warehouse.guid,
        hint: typeNames.join(", "),
        searchText: [warehouse.name, warehouse.code, warehouse.code_f2, warehouse.code_f1, typeNames.join(" "), posName].filter(Boolean).join(" "),
      };
    },
    removeChip(chip) {
      if (chip.kind === "pos") {
        this.update({ pos_ids: (this.modelValue.pos_ids || []).filter((id) => String(id) !== String(chip.value)) });
        return;
      }
      this.update({ warehouse_ids: (this.modelValue.warehouse_ids || []).filter((id) => String(id) !== String(chip.value)) });
    },
    pushGuidChips(chips, key, prefix) {
      const ids = this.modelValue[key] || [];
      if (!ids.length) return;
      if (ids.length === 1) {
        chips.push({ key, label: prefix + ": " + this.categoryName(ids[0]) });
        return;
      }
      chips.push({ key, label: prefix + ": " + ids.length });
    },
    removeFilter(key) {
      if (key === "search") {
        this.search = "";
        this.update({ search: "" });
      } else if (["pos_ids", "warehouse_ids", "product_guids", "product_category_guids", "accessory_category_guids", "warehouse_group_guids", "supplier_guids"].includes(key)) {
        this.update({ [key]: [] });
      } else if (key === "stock_filter") {
        this.update({ stock_filter: "all" });
      } else if (key === "web_available") {
        this.update({ web_available: false });
      } else if (key === "has_offer") {
        this.update({ has_offer: false });
      } else if (key === "vitrine_split") {
        this.update({ vitrine_split: false });
      } else if (key === "fushat_guid") {
        this.update({ fushat_guid: null, campaign_guid: null });
      } else {
        this.update({ [key]: null });
      }
    },
    clearAll() {
      this.search = "";
      this.update({
        search: "",
        pos_ids: [],
        warehouse_ids: [],
        product_guids: [],
        product_category_guids: [],
        accessory_category_guids: [],
        warehouse_group_guids: [],
        supplier_guids: [],
        warehouse_facility_id: null,
        fushat_guid: null,
        campaign_guid: null,
        web_available: false,
        has_offer: false,
        vitrine_split: false,
        as_of: null,
        stock_filter: "all",
      });
    },
    toggleVitrine() {
      const current = Number(this.modelValue.warehouse_facility_id);
      const next = current === Number(this.vitrineFacilityId) ? null : Number(this.vitrineFacilityId);
      this.update({ warehouse_facility_id: next, group_by: "warehouse", vitrine_split: false });
    },
    toggleVitrineSplit() {
      const next = !this.modelValue.vitrine_split;
      this.update({
        vitrine_split: next,
        warehouse_facility_id: next ? null : this.modelValue.warehouse_facility_id,
      });
    },
    categoryName(guid) {
      const found = this.categories.find((category) => category.guid === guid);
      return found ? found.name : guid;
    },
    buildOptions(relatedEntity) {
      const scoped = this.categories.filter((category) => category.related_entity === relatedEntity);
      const byParent = {};
      const guids = new Set(scoped.map((category) => category.guid));
      scoped.forEach((category) => {
        const parent = category.parent_id && guids.has(category.parent_id) ? category.parent_id : "__root__";
        (byParent[parent] = byParent[parent] || []).push(category);
      });

      const options = [];
      const walk = (parent, depth) => {
        (byParent[parent] || []).forEach((category) => {
          options.push({
            value: category.guid,
            label: "\u00a0\u00a0".repeat(depth) + (depth ? "└ " : "") + category.name,
            searchText: category.name,
          });
          walk(category.guid, depth + 1);
        });
      };
      walk("__root__", 0);
      return options;
    },
  },
};
</script>
